import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { useDesk } from '../lib/desk';
import type { HelpdeskSettings } from '../lib/types';
import { useToast } from './toast';
import { Button, Card, CardHeader, ErrorState, Input, PageLoader } from './ui';

/** The needs someone can ask this help desk for when they request an event setup: Tech needs, or Maintenance needs. */
export function EventNeedsTab() {
  const desk = useDesk();
  const canEdit = desk.can('ADMIN');
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['helpdesk-settings', desk.service], queryFn: () => api.get<HelpdeskSettings>('/helpdesk/settings', desk.query()) });
  const [needs, setNeeds] = useState<string[] | null>(null);
  const [adding, setAdding] = useState('');

  useEffect(() => {
    if (settings.data) setNeeds(settings.data.eventNeeds);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => api.put<{ eventNeeds: string[] }>(desk.path('/helpdesk/event-needs'), { eventNeeds: needs }),
    onSuccess: (saved) => {
      setNeeds(saved.eventNeeds);
      void queryClient.invalidateQueries({ queryKey: ['helpdesk-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['event-request-options'] });
      toast.success('Event needs saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (settings.error) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  if (settings.isLoading || !needs) return <PageLoader />;

  const add = (event: FormEvent) => {
    event.preventDefault();
    const name = adding.trim();
    if (!name) return;
    if (needs.some((need) => need.trim().toLowerCase() === name.toLowerCase())) {
      toast.error(`${name} is already on the list`);
      return;
    }
    setNeeds([...needs, name]);
    setAdding('');
  };

  const move = (index: number, by: -1 | 1) => {
    const next = [...needs];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    setNeeds(next);
  };

  const label = desk.facilities ? 'Maintenance needs' : 'Tech needs';

  return (
    <Card>
      <CardHeader
        title={label}
        description={`The ${label.toLowerCase()} people can choose when they request an event setup. A request that picks any of them opens a work order for ${desk.team}.`}
      />
      <div className="max-w-xl space-y-4 p-5">
        {needs.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing is on the list, so event requests can’t ask {desk.team} for anything.</p>
        ) : (
          <ul className="space-y-2">
            {needs.map((need, index) => (
              <li key={index} className="flex items-center gap-1.5">
                <Input
                  value={need}
                  disabled={!canEdit}
                  maxLength={60}
                  aria-label={`Need ${index + 1}`}
                  onChange={(event) => setNeeds(needs.map((item, i) => (i === index ? event.target.value : item)))}
                />
                {canEdit && (
                  <>
                    <Button size="sm" variant="ghost" icon={<ArrowUp />} disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Move ${need} up`} />
                    <Button size="sm" variant="ghost" icon={<ArrowDown />} disabled={index === needs.length - 1} onClick={() => move(index, 1)} aria-label={`Move ${need} down`} />
                    <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setNeeds(needs.filter((_, i) => i !== index))} aria-label={`Remove ${need}`} />
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <>
            <form onSubmit={add} className="flex gap-2">
              <Input value={adding} maxLength={60} onChange={(event) => setAdding(event.target.value)} placeholder={desk.facilities ? 'Add a need, e.g. Stage risers' : 'Add a need, e.g. Microphones'} />
              <Button type="submit" icon={<Plus />}>
                Add
              </Button>
            </form>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save {label.toLowerCase()}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
